import { rem, rgba } from 'polished';
import styled from 'styled-components/macro';

export const StyledAccountStatusLabel = styled.span<{ enabled: boolean }>`
  background-color: ${({ theme, enabled }) =>
    enabled
      ? rgba(theme.colors.cross.greenMint, 0.1)
      : rgba(theme.colors.cross.red, 0.1)};
  color: ${({ theme, enabled }) =>
    enabled ? theme.colors.cross.greenMint : theme.colors.cross.red};
  border-radius: ${rem('8px')};
  padding: ${rem('8px')} ${rem('16px')};
  font-weight: 600;

  display: inline-flex;
  align-items: center;
  gap: ${rem('8px')};
`;

export const StyledAccountStatusLabelsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem('8px')};
`;

export const StyledAddPaymentCta = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${rem('24px')} ${rem('16px')};
  background-color: transparent;
  border: ${rem('1px')} dashed ${({ theme }) => theme.colors.cross.grayson};
  border-radius: ${rem('8px')};
  color: ${({ theme }) => theme.colors.cross.grayson};
  font-size: ${rem('14px')};
  font-weight: 700;
  text-transform: uppercase;
  appearance: none;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) =>
      rgba(theme.colors.landlord.mainPale, 0.5)};
    border-color: ${({ theme }) => theme.colors.landlord.main};
  }
`;

export const StyledAddPaymentCtaIcon = styled.span`
  svg {
    width: ${rem('32px')};
    height: ${rem('32px')};

    path {
      fill: black;
    }
  }
`;

export const StyledAddPaymentCtaLabel = styled.span`
  display: flex;
  flex-direction: row;
  align-items: baseline;

  &::after {
    width: ${rem('24px')};
    height: ${rem('24px')};
    margin-left: ${rem('8px')};
    color: ${({ theme }) => theme.colors.main};
    font-size: ${rem('20px')};
    font-weight: 700;
    background-color: ${({ theme }) => theme.colors.landlord.main};
    border-radius: 100%;
    content: '+';
    overflow: hidden;
  }
`;

export const StyledCheckIcon = styled.div`
  display: block;
  width: ${rem('18px')};
  height: ${rem('10px')};
  border-radius: ${rem('2px')};
  border: ${rem('2px')} solid transparent;
  border-bottom-color: ${({ theme }) => theme.colors.cross.greenMint};
  border-left-color: ${({ theme }) => theme.colors.cross.greenMint};
  transform: translateY(-30%) rotate(-45deg);
`;

export const StyledHintQuestion = styled.button`
  width: 100%;
  margin: ${rem('8px')} 0 0 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.landlord.main};
  font-size: ${rem('14px')};
  font-weight: 400;
  text-align: left;
  text-decoration: underline;
  background-color: transparent;
  border: none;
  cursor: pointer;
  appearance: none;
`;

export const StyledHintAnswer = styled.div`
  margin-top: ${rem('8px')};

  ul {
    margin: 0;
    padding-left: ${rem('16px')};
    color: ${({ theme }) => theme.colors.cross.grayson};
    font-size: ${rem('14px')};
  }
`;

export const StyledCrossIcon = styled.div`
  display: block;
  width: ${rem('18px')};
  height: ${rem('18px')};
  position: relative;

  &::after,
  &::before {
    content: '';
    width: ${rem('2px')};
    height: 100%;
    border-radius: ${rem('2px')};
    position: absolute;
    top: 50%;
    left: 50%;
    background-color: ${({ theme }) => theme.colors.cross.red};
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }
`;

export const StyledError = styled.p`
  color: ${({ theme }) => theme.colors.cross.red};
  font-size: ${rem('14px')};
  line-height: 1.4;
`;

export const StyledLabel = styled.label`
  display: flex;
  margin-bottom: ${rem('8px')};
  color: ${({ theme }) => theme.colors.cross.grayson};
  font-size: ${rem('16px')};
  line-height: 1.4;
`;

export const StyledPaymentsList = styled.ul`
  margin: 0;
  padding: 0;
`;

export const StyledModalFieldList = styled.ul`
  margin: 0;
  padding: 0;
`;

export const StyledModalFieldListItem = styled.li`
  list-style: none;

  &:not(:first-child) {
    margin-top: ${rem('16px')};
  }
`;

export const StyledModalFieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: none;
`;

export const StyledModalFooter = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: ${rem('16px')};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    justify-content: end;
  }

  button {
    min-width: 100%;

    @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
      min-width: ${rem('200px')};
    }
  }
`;

export const StyledModalHeader = styled.header`
  text-align: center;
`;

export const StyledModalInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    width: ${rem('480px')};
  }
`;

export const StyledModalSpinnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  transform: translateY(-1px);
`;

export const StyledParagraph = styled.p`
  color: ${({ theme }) => theme.colors.cross.grayson};
  font-size: ${rem('16px')};
  line-height: 1.4;

  a {
    color: ${({ theme }) => theme.colors.landlord.main};
    text-decoration: none;
  }
`;

export const StyledPaymentsListItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${rem('20px')};
  padding: ${rem('16px')};
  border-radius: ${rem('8px')};
  background-color: ${({ theme }) => theme.colors.main};
  box-shadow: ${({ theme }) =>
    `0 0 ${rem('8px')} ${rgba(theme.colors.cross.silverFox, 0.5)}`};
  list-style: none;
`;

export const StyledPaymentsListItemActive = styled.span`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: ${rem('4px')};
  color: ${({ theme }) => theme.colors.cross.greenMint};

  ${StyledCheckIcon} {
    position: relative;
    top: ${rem('-2px')};
    width: ${rem('15px')};
    height: ${rem('8px')};
  }
`;

export const StyledPaymentsListItemCta = styled.button`
  display: flex;
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.landlord.main};
  font-size: ${rem('16px')};
  font-weight: 700;
  text-decoration: underline;
  background-color: transparent;
  border: none;
  cursor: pointer;
  appearance: none;

  &:disabled {
    opacity: 0.3;
    cursor: auto;
  }
`;

export const StyledPaymentsListItemText = styled.span`
  display: flex;
  flex-direction: column;
  font-size: ${rem('14px')};
  font-weight: 400;

  strong {
    color: ${({ theme }) => theme.colors.cross.black};
    font-weight: inherit;
  }

  em {
    color: ${({ theme }) => theme.colors.cross.grayson};
    font-style: normal;
    line-height: 1.6;
  }
`;

export const StyledSection = styled.section`
  margin-bottom: ${rem('32px')};
`;

export const StyledSubtitle = styled.h4`
  display: flex;
  gap: ${rem('16px')};
  align-items: center;
  font-size: ${rem('16px')};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.cross.black};
  margin-bottom: ${rem('16px')};
  text-align: left;
  position: relative;
`;

export const StyledTitle = styled.h4`
  margin: 0 0 ${rem('32px')} 0;
  color: ${({ theme }) => theme.colors.cross.black};
  font-size: ${rem('20px')};
  font-weight: 600;
`;

export const StyledWrapper = styled.div`
  width: 100%;
  margin: ${rem('48px')} 0 ${rem('16px')} 0;
  padding-top: ${rem('48px')};
  border-top: 1px solid ${({ theme }) => theme.colors.cross.lightGray};
`;
